import type { Playable } from "./audioSequence";
import { clamp } from "./utils";

export type ComputeEpisodePercentageArgs = {
  progress: number;
  duration: number;
  playable: Pick<Playable, "getOffsetForTrack">;
  trailerTrackCount: number;
  outroTrackCount: number;
  totalTrackCount: number;
  mainStartSeconds?: number;
  mainEndSeconds?: number;
};

type EpisodePlaybackLayoutSource = {
  tracks: ReadonlyArray<unknown>;
  fallbackTracks?: ReadonlyArray<unknown>;
  compositeAudioTimeline?: { mainStartSeconds: number; mainEndSeconds: number };
  trailerTrackCount?: number;
  outroTrackCount?: number;
  fallbackTrailerTrackCount?: number;
  fallbackOutroTrackCount?: number;
};

export type EpisodePlaybackLayout = Pick<
  ComputeEpisodePercentageArgs,
  "trailerTrackCount" | "outroTrackCount" | "totalTrackCount" | "mainStartSeconds" | "mainEndSeconds"
>;

export type BookendPlacement = "read-in" | "read-out";

export function getActiveEpisodePlaybackLayout(
  devo: EpisodePlaybackLayoutSource,
  usingFallbackSequence: boolean,
): EpisodePlaybackLayout {
  if (usingFallbackSequence && devo.fallbackTracks?.length) {
    return {
      outroTrackCount: devo.fallbackOutroTrackCount ?? 0,
      trailerTrackCount: devo.fallbackTrailerTrackCount ?? 0,
      totalTrackCount: devo.fallbackTracks.length,
    };
  }
  return {
    ...(devo.compositeAudioTimeline
      ? {
          mainEndSeconds: devo.compositeAudioTimeline.mainEndSeconds,
          mainStartSeconds: devo.compositeAudioTimeline.mainStartSeconds,
        }
      : {}),
    outroTrackCount: devo.outroTrackCount ?? 0,
    trailerTrackCount: devo.trailerTrackCount ?? 0,
    totalTrackCount: devo.tracks.length,
  };
}

export type EpisodePlaybackBounds = {
  mainStartOffset: number;
  mainEndOffset: number;
  mainDuration: number;
};

export type EpisodePlaybackProgress = EpisodePlaybackBounds & {
  mainProgressSeconds: number;
  percentage: number;
  hasReachedMainEnd: boolean;
};

export function getEpisodePlaybackBounds(args: ComputeEpisodePercentageArgs): EpisodePlaybackBounds {
  const { duration, playable, trailerTrackCount, outroTrackCount, totalTrackCount, mainStartSeconds, mainEndSeconds } =
    args;
  const sequenceDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
  if (
    typeof mainStartSeconds === "number" &&
    Number.isFinite(mainStartSeconds) &&
    mainStartSeconds >= 0 &&
    typeof mainEndSeconds === "number" &&
    Number.isFinite(mainEndSeconds) &&
    mainEndSeconds > mainStartSeconds
  ) {
    const normalizedStart = clamp(mainStartSeconds, 0, sequenceDuration);
    const normalizedEnd = clamp(mainEndSeconds, normalizedStart, sequenceDuration);
    return {
      mainStartOffset: normalizedStart,
      mainEndOffset: normalizedEnd,
      mainDuration: Math.max(normalizedEnd - normalizedStart, 0),
    };
  }
  const trailerDuration = playable.getOffsetForTrack(trailerTrackCount);
  const normalizedTrailerDuration = clamp(trailerDuration, 0, sequenceDuration);
  const outroStartIndex = clamp(totalTrackCount - outroTrackCount, 0, totalTrackCount);
  const outroStartOffset = outroTrackCount > 0 ? playable.getOffsetForTrack(outroStartIndex) : sequenceDuration;
  const normalizedOutroStart = clamp(outroStartOffset, normalizedTrailerDuration, sequenceDuration);

  return {
    mainStartOffset: normalizedTrailerDuration,
    mainEndOffset: normalizedOutroStart,
    mainDuration: Math.max(normalizedOutroStart - normalizedTrailerDuration, 0),
  };
}

export function getSequencePositionForEpisodePercentage(
  args: Omit<ComputeEpisodePercentageArgs, "progress"> & { percentage: number },
): number {
  const percentage = Number.isFinite(args.percentage) ? clamp(args.percentage, 0, 100) : 0;
  if (percentage === 0) return 0;
  const bounds = getEpisodePlaybackBounds({ ...args, progress: 0 });
  return bounds.mainStartOffset + (bounds.mainDuration * percentage) / 100;
}

type PlaybackRecoveryTimeline = EpisodePlaybackLayout & {
  duration: number;
  playable: Pick<Playable, "getOffsetForTrack">;
};

type PlaybackRecoveryPositionArgs = {
  previous: PlaybackRecoveryTimeline & { position: number };
  next: PlaybackRecoveryTimeline;
};

function mapSegmentPosition(
  position: number,
  previousStart: number,
  previousEnd: number,
  nextStart: number,
  nextEnd: number,
): number {
  const previousDuration = Math.max(previousEnd - previousStart, 0);
  const nextDuration = Math.max(nextEnd - nextStart, 0);
  if (previousDuration === 0 || nextDuration === 0) {
    return nextStart;
  }

  const segmentPercentage = clamp((position - previousStart) / previousDuration, 0, 1);
  return nextStart + nextDuration * segmentPercentage;
}

export function getBookendTrackRange(
  layout: EpisodePlaybackLayout,
  playable: Pick<Playable, "getOffsetForTrack">,
  placement: BookendPlacement,
  assetIndex: number,
): { start: number; end: number } | null {
  const count = placement === "read-in" ? layout.trailerTrackCount : layout.outroTrackCount;
  if (assetIndex < 0 || assetIndex >= count) {
    return null;
  }

  const firstTrackIndex = placement === "read-in" ? 0 : layout.totalTrackCount - count;
  const trackIndex = firstTrackIndex + assetIndex;
  return {
    start: playable.getOffsetForTrack(trackIndex),
    end: playable.getOffsetForTrack(trackIndex + 1),
  };
}

export function getBookendAssetIndex(
  layout: EpisodePlaybackLayout,
  playable: Pick<Playable, "getOffsetForTrack">,
  placement: BookendPlacement,
  position: number,
): number {
  const count = placement === "read-in" ? layout.trailerTrackCount : layout.outroTrackCount;
  for (let index = 0; index < count; index += 1) {
    const range = getBookendTrackRange(layout, playable, placement, index);
    if (range && position >= range.start && position < range.end) {
      return index;
    }
  }
  return -1;
}

function getRecoveryBookendRange(
  timeline: PlaybackRecoveryTimeline,
  bounds: EpisodePlaybackBounds,
  placement: BookendPlacement,
  assetIndex: number,
): { start: number; end: number } | null {
  if (timeline.mainStartSeconds === undefined) {
    return getBookendTrackRange(timeline, timeline.playable, placement, assetIndex);
  }

  return {
    start: placement === "read-in" ? 0 : bounds.mainEndOffset,
    end: placement === "read-in" ? bounds.mainStartOffset : timeline.duration,
  };
}

/**
 * Maps recovery onto the same logical main position or bookend ordinal. This
 * prevents a source fallback during a later bookend from replaying earlier
 * bookends merely because main-audio progress is already 0% or 100%.
 */
export function getSequencePositionForPlaybackRecovery({ previous, next }: PlaybackRecoveryPositionArgs): number {
  const previousBounds = getEpisodePlaybackBounds({ ...previous, progress: previous.position });
  const nextBounds = getEpisodePlaybackBounds({ ...next, progress: 0 });

  if (previous.position >= previousBounds.mainStartOffset && previous.position < previousBounds.mainEndOffset) {
    return mapSegmentPosition(
      previous.position,
      previousBounds.mainStartOffset,
      previousBounds.mainEndOffset,
      nextBounds.mainStartOffset,
      nextBounds.mainEndOffset,
    );
  }

  const placement = previous.position < previousBounds.mainStartOffset ? "read-in" : "read-out";
  const previousAssetIndex =
    previous.mainStartSeconds !== undefined
      ? 0
      : getBookendAssetIndex(previous, previous.playable, placement, previous.position);
  const previousRange = getRecoveryBookendRange(previous, previousBounds, placement, previousAssetIndex);
  const nextRange = getRecoveryBookendRange(next, nextBounds, placement, previousAssetIndex);

  if (!previousRange || !nextRange) {
    return placement === "read-in" ? nextBounds.mainStartOffset : nextBounds.mainEndOffset;
  }

  return mapSegmentPosition(previous.position, previousRange.start, previousRange.end, nextRange.start, nextRange.end);
}

export function getEpisodePlaybackProgress(args: ComputeEpisodePercentageArgs): EpisodePlaybackProgress {
  const { progress } = args;
  const bounds = getEpisodePlaybackBounds(args);
  const mainProgressSeconds = clamp(progress - bounds.mainStartOffset, 0, bounds.mainDuration);
  const percentage =
    bounds.mainDuration === 0 ? 0 : clamp(Math.floor((mainProgressSeconds / bounds.mainDuration) * 100), 0, 100);

  return {
    ...bounds,
    mainProgressSeconds,
    percentage,
    hasReachedMainEnd: bounds.mainDuration > 0 && progress >= bounds.mainEndOffset,
  };
}

export function computeEpisodePercentage(args: ComputeEpisodePercentageArgs): number {
  return getEpisodePlaybackProgress(args).percentage;
}
